import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { PORTFOLIO } from "@/constants/testIds";
import { X, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { tagLabel, orderTags, canonicalTag } from "@/i18n/portfolioTags";

export default function PortfolioGrid() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, []);

  const pick = (v) => (v && typeof v === "object" ? (v[lang] || v.en || v.ru || v.es || "") : (v || ""));

  const tags = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return ["all", ...orderTags(Array.from(set))];
  }, [projects]);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? projects
        : projects.filter((p) => (p.tags || []).map(canonicalTag).includes(filter)),
    [filter, projects]
  );

  return (
    <section id="portfolio" className="relative bg-[#f5f5f5] py-24 lg:py-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div>
            {/* kicker desconectado 22-09-2026 (pedido del estudio). Reactivar: poner el flag en true */}
            {false && (
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3">
                · {t.portfolio.label}
              </div>
            )}
            <h2 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-[#0a0a0a]">
              {t.portfolio.title}
            </h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {tags.map((tg) => (
              <button
                key={tg}
                data-testid={PORTFOLIO.filter(tg)}
                onClick={() => setFilter(tg)}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
                  filter === tg
                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                    : "bg-transparent text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                }`}
              >
                {tg === "all" ? t.portfolio.filter_all : `#${tagLabel(tg, lang)}`}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((p, i) => (
            <motion.button
              key={p.project_id}
              data-testid={PORTFOLIO.card(i)}
              onClick={() => setActive(p)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="portfolio-card group relative text-left bg-white border border-[#e5e5e5] hover:border-[#0a0a0a] transition-colors overflow-hidden"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#eeeeee]">
                {p.thumbnail ? (
                  <img src={p.thumbnail} alt={p.title} className="portfolio-img w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${p.color_a}, ${p.color_b})` }} />
                )}
                {/* Corner index */}
                <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-[#0a0a0a]/80 px-2 py-1">
                  {String(i + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}
                </div>
                {/* Accent corner lines on hover */}
                <span className="absolute top-0 left-0 w-0 h-px bg-[#00d4ff] group-hover:w-full transition-all duration-700" />
                <span className="absolute bottom-0 right-0 w-0 h-px bg-[#ff006e] group-hover:w-full transition-all duration-700" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a]">{pick(p.category)}</div>
                    <h3 className="mt-1 font-mono font-medium text-lg text-[#0a0a0a]">{pick(p.title)}</h3>
                  </div>
                  <ArrowUpRight size={18} className="text-[#0a0a0a] mt-1 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.tags || []).map((tg) => (
                    <span key={tg} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#4a4a4a] border border-[#e5e5e5] px-2 py-1">
                      #{tagLabel(tg, lang)}
                    </span>
                  ))}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <ModalBoundary onReset={() => setActive(null)}>
            <ProjectModal project={active} onClose={() => setActive(null)} pick={pick} />
          </ModalBoundary>
        )}
      </AnimatePresence>
    </section>
  );
}

// Parámetros del reproductor: sin título, sin autor, sin retrato, sin badge de Vimeo
// y en bucle (loop=1 reinicia al terminar, así tampoco aparece la pantalla de recomendaciones).
const VIMEO_OPTS = "title=0&byline=0&portrait=0&badge=0&autopause=0&dnt=1&loop=1";

// Convierte un link de Vimeo/YouTube en su versión embebible y limpia.
function toEmbed(url) {
  const u = String(url || "").trim().replace(/&amp;/g, "&");
  if (!u) return "";
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt && !u.includes("vimeo")) return `https://www.youtube.com/embed/${yt[1]}?rel=0&loop=1&playlist=${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\?([^#\s]*))?/);
  if (vm) {
    // Ojo: el codigo de insertar de Vimeo trae los parametros con &amp;. Si no se
    // normaliza, el hash "h" no se encuentra y el video no listado no se ve.
    const params = new URLSearchParams((vm[2] || "").replace(/&amp;/g, "&"));
    const h = params.get("h");
    return `https://player.vimeo.com/video/${vm[1]}?${h ? `h=${h}&` : ""}${VIMEO_OPTS}`;
  }
  return "";
}

// Barrera de error: si el modal falla, NO se cae toda la pagina (React desmonta todo el arbol
// y queda la pantalla en blanco). Muestra el error y deja cerrar.
class ModalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    // queda en la consola para poder diagnosticarlo
    console.error("[kuryarti] error en la ficha del proyecto:", err, info);
  }

  render() {
    if (this.state.err) {
      const msg = String((this.state.err && this.state.err.message) || this.state.err || "Error desconocido");
      return (
        <div className="fixed inset-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md flex items-center justify-center p-6">
          <div className="bg-white border border-[#0a0a0a] w-full max-w-lg p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
              Error al mostrar el proyecto
            </div>
            <p className="mt-3 font-mono text-xs text-[#0a0a0a] break-words leading-relaxed">{msg}</p>
            <button
              onClick={this.props.onReset}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3 bg-[#0a0a0a] text-white border border-[#0a0a0a]"
            >
              Cerrar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProjectModal({ project, onClose, pick }) {
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const touchRef = useRef(null);

  // Viñetas del caso: primero el video, después la portada y el material de proceso.
  const slides = useMemo(() => {
    const out = [];
    const embed = toEmbed(project.video_url);
    if (embed) {
      out.push({
        kind: "video",
        url: embed,
        label: t.portfolio.intro,
        duration: project.video_duration || "",
        text: "",
      });
    }
    const embed2 = toEmbed(project.video_url_2);
    if (embed2) {
      out.push({
        kind: "video",
        url: embed2,
        label: t.portfolio.outro,
        duration: project.video_duration_2 || "",
        text: "",
      });
    }
    // La portada (thumbnail) NO va como viñeta: ya se ve en la tarjeta de la grilla.
    (project.process_images || []).forEach((u, i) => {
      const nombre = pick((project.process_titles || [])[i]);
      out.push({ kind: "image", url: u, label: nombre || t.portfolio.gallery, text: "" });
    });
    return out;
  }, [project, t, pick]);

  const total = slides.length;
  const current = slides[idx];
  // Circular: al pasar la última vuelve a la primera, y al retroceder desde la primera va a la última.
  const go = (d) => setIdx((i) => (total > 0 ? (i + d + total) % total : 0));

  useEffect(() => {
    setIdx(0);
  }, [project]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      else if (e.key === "ArrowRight") setIdx((i) => (total > 0 ? (i + 1) % total : 0));
      else if (e.key === "ArrowLeft") setIdx((i) => (total > 0 ? (i - 1 + total) % total : 0));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, total]);

  return (
    <motion.div
      data-testid={PORTFOLIO.modal}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.4 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl mx-auto my-6 lg:my-12 bg-white border border-[#0a0a0a]"
      >
        <button
          onClick={onClose}
          data-testid={PORTFOLIO.modalClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div
          className={`aspect-[16/9] relative overflow-hidden ${
            current && current.kind === "video" ? "bg-black" : "bg-white"
          }`}
          onTouchStart={(e) => { touchRef.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchRef.current === null) return;
            const dx = e.changedTouches[0].clientX - touchRef.current;
            if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
            touchRef.current = null;
          }}
        >
          {current ? (
            current.kind === "video" ? (
              <iframe
                src={current.url}
                title={pick(project.title)}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
              />
            ) : (
              <img src={current.url} alt={pick(project.title)} className="w-full h-full object-contain" />
            )
          ) : (
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${project.color_a}, ${project.color_b})` }} />
          )}

          {total > 1 && (
            <>
              <button
                onClick={() => go(-1)}
                aria-label="Previous"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-white/90 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => go(1)}
                aria-label="Next"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-white/90 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-[#0a0a0a]/80 px-2 py-1">
                {idx + 1} / {total}
              </div>
            </>
          )}
        </div>

        {current && (
          <div className="px-6 lg:px-10 pt-5 pb-5 border-b border-[#e5e5e5]">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
              {current.label}{current.duration ? ` · ${current.duration}` : ""}
            </div>
            {current.text ? (
              <p className="mt-2 font-sans text-sm text-[#4a4a4a] leading-relaxed max-w-2xl">{current.text}</p>
            ) : null}
          </div>
        )}

        <div className="p-6 lg:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">{pick(project.category)}</div>
          <h3 className="mt-2 font-mono font-bold tracking-tighter text-3xl lg:text-4xl text-[#0a0a0a]">{pick(project.title)}</h3>
          {/* La descripción del caso se ve SIEMPRE (antes solo aparecía en la viñeta del video) */}
          <p className="mt-6 font-sans text-base text-[#4a4a4a] leading-relaxed max-w-2xl">{pick(project.description)}</p>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Meta label={t.portfolio.client} value={project.client} />
            <Meta label={t.portfolio.year} value={project.year} />
            <Meta
              label={t.portfolio.duration}
              value={
                project.video_duration && project.video_duration_2
                  ? `${t.portfolio.intro} ${project.video_duration} · ${t.portfolio.outro} ${project.video_duration_2}`
                  : project.duration
              }
            />
            <Meta label={t.portfolio.tools} value={(project.tools || []).join(", ")} />
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {(project.tags || []).map((tg) => (
              <span key={tg} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0a0a0a] border border-[#0a0a0a] px-3 py-1.5">
                #{tagLabel(tg, lang)}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">{label}</div>
    <div className="mt-1 font-mono text-sm text-[#0a0a0a]">{value || "—"}</div>
  </div>
);
